/**
 * The campaign monitoring screens' copy: the overview, the agenda and the failures list. Each
 * failure reason has one sentence here, read by the overview's preview and by the failures list.
 */

import { defineCatalogue } from '../../../i18n';

export const monitoringCatalogue = defineCatalogue(
  {
    title: 'Monitoramento e agendas',
    'nav.label': 'Monitoramento',
    'nav.overview': 'Visão geral',

    'stats.completed': 'Coletas concluídas hoje',
    'stats.failed': 'Coletas que falharam hoje',
    'stats.creditsUsed': 'Créditos consumidos hoje',

    'stats.predictedHourly': 'Observações previstas · 1h',
    'stats.predictedDaily': 'Observações previstas · 1d',
    'stats.consumptionHourly': 'Consumo estimado · 1h',
    'stats.consumptionDaily': 'Consumo estimado · 1d',

    'failures.title': 'Coletas que falharam hoje',
    'failures.viewAll': 'Ver todas',
    'failures.empty.title': 'Nenhuma coleta falhou hoje',

    'missedSlot.title': 'O primeiro horário perdido',
    'missedSlot.reason': 'Seu saldo foi insuficiente para esta leitura.',

    'volume.title': 'Volume coletado',
    'volume.window': 'últimas 24 h',
    'volume.empty.title': 'Ainda sem dados de volume',
    'volume.series.credits': 'Créditos',
    'volume.now': 'agora',

    'agenda.title': 'Agenda de coleta',
    'agenda.viewAll': 'Ver toda a agenda',
    'agenda.empty.title': 'Nada agendado no momento',
    'agenda.absentWindow.title': 'A agenda ainda não tem histórico',
    'agenda.absentWindow.description':
      'Aqui aparecem as coletas de hoje em diante, assim que forem planejadas.',
    'agenda.window': 'A agenda guarda o registro a partir de {when}',
    'agenda.windowSelect.label': 'Janela',
    'agenda.windowSelect.next24h': 'Próximas 24 h',
    'agenda.windowSelect.today': 'Hoje',
    'agenda.windowSelect.sinceEarliest': 'Desde o início do histórico',
    'agenda.dayPicker.label': 'Dia',
    'agenda.legend.done': 'coleta feita',
    'agenda.legend.planned': 'agendada',
    'agenda.legend.dispatched': 'em andamento',
    'agenda.legend.failed': 'falhou',
    'agenda.legend.noCredit': 'sem crédito',
    'agenda.legend.label': 'Legenda',
    'slot.scheduled.one': '{count} coleta agendada',
    'slot.scheduled.other': '{count} coletas agendadas',
    'slot.noCredit.one': '{count} coleta sem crédito',
    'slot.noCredit.other': '{count} coletas sem crédito',
    'slot.done.one': '{done} de {count} coleta feita',
    'slot.done.other': '{done} de {count} coletas feitas',
    'slot.failed.one': '{count} falhou',
    'slot.failed.other': '{count} falharam',

    'day.today': 'Hoje',
    'day.tomorrow': 'Amanhã',
    'day.yesterday': 'Ontem',

    'failures.page.title': 'Coletas que falharam',
    'failures.page.count': '{count} nos últimos 7 dias',
    'failures.page.rowTitle': '{account} · {time}',
    'failures.page.empty.title': 'Nenhuma coleta falhou nos últimos 7 dias',
    'agenda.noCredit': 'sem crédito',

    'status.planned': 'agendada',
    'status.dispatched': 'em andamento',
    'status.done': 'feita',
    'status.failed': 'falhou',
    'status.noCredit': 'sem crédito',

    'reason.budget_shed': 'Faltaram créditos para ler esta conta; adicione créditos',
    'reason.budget_window': 'Este horário ficou sem créditos; adicione créditos para os próximos',
    'reason.budget_hold': 'Sem créditos disponíveis na hora da leitura; adicione créditos',
    'reason.collection_cancelled': 'Substituída por uma leitura mais recente',
    'reason.provider_unavailable': 'A rede social não respondeu; não há nada a fazer',
    'reason.provider_not_found': 'Conta não encontrada; confira se ficou privada ou mudou de nome',
    'reason.collection_failed': 'Não foi possível concluir esta leitura; não há nada a fazer',

    'jobType.posts': 'Publicações',
    'jobType.comments': 'Comentários',
    'jobType.stories': 'Stories',
    'jobType.audio_fanout': 'Áudio',
    'jobType.profile_tracked': 'Perfil',

    'audio.title': 'Áudio',
    'audio.empty.title': 'Nenhum som encontrado ainda',
    'audio.empty.description':
      'Os sons aparecem aqui assim que as contas desta organização publicarem reels com o áudio ativado no perfil de coleta.',
    'audio.original': 'Som original',
    'audio.untitled': 'Som sem título',
    'audio.notLookedUpYet': 'Ainda não lido',
    'audio.noNextLookup': 'Sem próxima leitura',
    'audio.noReuse': 'Nenhum reaproveitamento encontrado',
    'audio.column.title': 'Som',
    'audio.column.lastLookup': 'Última leitura',
    'audio.column.reuses': 'Reaproveitamentos lidos',
    'audio.column.nextLookup': 'Próxima leitura',
    'audio.column.sources': 'Publicações de origem',
    'audio.column.matches': 'Reaproveitado por',
    'audio.match.row': '{account} · {when} · {basis}',
    'audio.match.sameVideo': 'mesmo vídeo',
    'audio.match.sameAccount': 'mesma conta',
    'audio.match.sameSound': 'mesmo som',
  },
  {
    title: 'Monitoring and schedules',
    'nav.label': 'Monitoring',
    'nav.overview': 'Overview',

    'stats.completed': 'Collections completed today',
    'stats.failed': 'Collections that failed today',
    'stats.creditsUsed': 'Credits spent today',

    'stats.predictedHourly': 'Predicted observations · 1h',
    'stats.predictedDaily': 'Predicted observations · 1d',
    'stats.consumptionHourly': 'Estimated consumption · 1h',
    'stats.consumptionDaily': 'Estimated consumption · 1d',

    'failures.title': 'Collections that failed today',
    'failures.viewAll': 'View all',
    'failures.empty.title': 'No collection failed today',

    'missedSlot.title': 'The first missed time',
    'missedSlot.reason': 'Your balance was not enough for this read.',

    'volume.title': 'Volume collected',
    'volume.window': 'last 24 h',
    'volume.empty.title': 'No volume data yet',
    'volume.series.credits': 'Credits',
    'volume.now': 'now',

    'agenda.title': 'Collection agenda',
    'agenda.viewAll': 'View the whole agenda',
    'agenda.empty.title': 'Nothing scheduled right now',
    'agenda.absentWindow.title': 'The agenda has no history yet',
    'agenda.absentWindow.description':
      'Collections from today onwards appear here as soon as they are planned.',
    'agenda.window': 'The agenda keeps its record from {when}',
    'agenda.windowSelect.label': 'Window',
    'agenda.windowSelect.next24h': 'Next 24 h',
    'agenda.windowSelect.today': 'Today',
    'agenda.windowSelect.sinceEarliest': 'Since the earliest record',
    'agenda.dayPicker.label': 'Day',
    'agenda.legend.done': 'collection done',
    'agenda.legend.planned': 'scheduled',
    'agenda.legend.dispatched': 'in progress',
    'agenda.legend.failed': 'failed',
    'agenda.legend.noCredit': 'no credit',
    'agenda.legend.label': 'Legend',
    'slot.scheduled.one': '{count} collection scheduled',
    'slot.scheduled.other': '{count} collections scheduled',
    'slot.noCredit.one': '{count} collection without credit',
    'slot.noCredit.other': '{count} collections without credit',
    'slot.done.one': '{done} of {count} collection done',
    'slot.done.other': '{done} of {count} collections done',
    'slot.failed.one': '{count} failed',
    'slot.failed.other': '{count} failed',

    'day.today': 'Today',
    'day.tomorrow': 'Tomorrow',
    'day.yesterday': 'Yesterday',

    'failures.page.title': 'Collections that failed',
    'failures.page.count': '{count} in the last 7 days',
    'failures.page.rowTitle': '{account} · {time}',
    'failures.page.empty.title': 'No collection failed in the last 7 days',
    'agenda.noCredit': 'no credit',

    'status.planned': 'scheduled',
    'status.dispatched': 'in progress',
    'status.done': 'done',
    'status.failed': 'failed',
    'status.noCredit': 'no credit',

    'reason.budget_shed': 'Not enough credits to read this account; add credits',
    'reason.budget_window': 'This time had no credits; add credits for the next ones',
    'reason.budget_hold': 'No credits available when the read was due; add credits',
    'reason.collection_cancelled': 'Superseded by a more recent read',
    'reason.provider_unavailable': 'The social network did not respond; nothing to do',
    'reason.provider_not_found': 'Account not found; check whether it went private or was renamed',
    'reason.collection_failed': 'This read could not be completed; nothing to do',

    'jobType.posts': 'Posts',
    'jobType.comments': 'Comments',
    'jobType.stories': 'Stories',
    'jobType.audio_fanout': 'Audio',
    'jobType.profile_tracked': 'Profile',

    'audio.title': 'Audio',
    'audio.empty.title': 'No sounds found yet',
    'audio.empty.description':
      "Sounds show up here once this organisation's tracked accounts post reels with audio tracking on in the collection profile.",
    'audio.original': 'Original sound',
    'audio.untitled': 'Untitled sound',
    'audio.notLookedUpYet': 'Not looked up yet',
    'audio.noNextLookup': 'No next lookup',
    'audio.noReuse': 'No reuse found',
    'audio.column.title': 'Sound',
    'audio.column.lastLookup': 'Last lookup',
    'audio.column.reuses': 'Reuses read',
    'audio.column.nextLookup': 'Next lookup',
    'audio.column.sources': 'Source posts',
    'audio.column.matches': 'Reused by',
    'audio.match.row': '{account} · {when} · {basis}',
    'audio.match.sameVideo': 'same video',
    'audio.match.sameAccount': 'same account',
    'audio.match.sameSound': 'same sound',
  },
);

export const cm = monitoringCatalogue.t;
export const cmPlural = monitoringCatalogue.plural;
